import { DONE_BUTTON } from "../../exercises/homework-done.ts";

/**
 * Предпросмотр перед нажатием (5.3 docs/homework-phase2-design.md).
 *
 * Отдельная панель над кнопкой, а не подпись под ней: последнее, что человек
 * видит перед необратимым действием, обязано быть списком имён.
 *
 * ⚠️ Часовой пояс в строке срока это пояс ПРЕПОДАВАТЕЛЯ, и подпись обязательна
 * именно при мультивыдаче. Сам вопрос решён фазой 1 (`due_at` это один момент,
 * каждый видит его в своей зоне), но при двенадцати получателях он становится
 * виден: часть учеников не в Москве, и строка «Срок: 20 августа, 18:00» без
 * подписи зоны читается как обещание, что у всех восемнадцать.
 */

const NAME_LIMIT = 4;

/**
 * Что уйдёт ученику. Две ветки, потому что обещание разное: заготовка это одни
 * и те же задания у всех, генератор это своё задание у каждого.
 *
 * ⚠️ Веток ДВЕ, а не три, хотя источников три: у математики и английского
 * обещание одно и то же слово в слово. Предмет здесь не называется намеренно,
 * его человек только что выбрал радиокнопкой, и повторять выбор в
 * предпросмотре значит тратить последнюю строку перед необратимым нажатием на
 * то, что и так известно.
 */
export type PreviewTasks =
  | { kind: "plan"; count: number; planTitle: string }
  | {
      kind: "topics";
      count: number;
      topics: readonly { name: string; level: string; count: number }[];
    };

function namesLine(names: string[]): string {
  if (names.length <= NAME_LIMIT) return names.join(", ");
  return `${names.slice(0, NAME_LIMIT).join(", ")}, +${names.length - NAME_LIMIT}`;
}

function dueLine(dueAt: number, timeZone: string): string {
  const fmt = new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  });
  return `${fmt.format(new Date(dueAt))} (${timeZone})`;
}

function tasksLine(tasks: PreviewTasks): string {
  if (tasks.kind === "plan") return `Задания (${tasks.count}): из заготовки «${tasks.planTitle}»`;
  const parts = tasks.topics.map((t) => `${t.name}, ${t.level} (${t.count})`).join(" · ");
  return `Задания (${tasks.count}): ${parts}`;
}

export function AssignPreview(props: {
  names: string[];
  /**
   * `null` у домашки без заданий, из одного материала (1.4
   * docs/homework-scratch-design.md). Обещание тогда другое: выполненной её
   * делает сам ученик, и сказать это надо до нажатия.
   */
  tasks: PreviewTasks | null;
  /**
   * «Материал: текст задания, 2 картинки и 1 аудиозапись; ссылки в тексте:
   * fipi.ru». `null`, если материала нет. Хосты ссылок здесь проверка до
   * нажатия, что адрес распознан (Р10 docs/homework-media-plan.md).
   */
  materialLine: string | null;
  dueAt: number | null;
  timeZone: string | null;
  /** Подпись «задана после занятия» уходит одному ученику или снята вовсе. */
  lessonNote: string | null;
  /**
   * Почему темы стоят в таком порядке. Есть только у листа, пришедшего адресом
   * из панели «Где дыры» (6.3 docs/student-errors-design.md).
   *
   * ⚠️ Подпись обязательна и не является украшением: такой лист собран по
   * темам, на которых стоит названная, поэтому «по квадратным уравнениям»
   * начинается с дробей. Без строки преподаватель решит, что форма перепутала
   * тему, и переберёт состав руками.
   */
  orderNote: string | null;
}) {
  return (
    <div className="assign__preview">
      <p>
        <strong>Домашку {props.names.length === 1 ? "получит" : "получат"}:</strong>{" "}
        {namesLine(props.names)}
      </p>
      {props.materialLine !== null && <p>{props.materialLine}</p>}
      <p>
        {props.tasks === null
          ? `Заданий с проверкой нет: домашка будет сдана, когда ученик напишет ответ, приложит фото или нажмёт «${DONE_BUTTON}».`
          : tasksLine(props.tasks)}
      </p>
      {props.orderNote !== null && <p className="muted">{props.orderNote}</p>}
      <p>
        Срок:{" "}
        {props.dueAt === null || props.timeZone === null
          ? "без срока"
          : dueLine(props.dueAt, props.timeZone)}
      </p>
      {props.lessonNote !== null && <p className="muted">{props.lessonNote}</p>}
      {/*
        Предупреждение стоит ДО кнопки, а не в подтверждении: «работа
        нескольким» естественно читается как «одна работа на класс», и это
        неправда, а узнать об этом человек обязан до нажатия.

        ⚠️ Приём пришёл от пачки уроков нескольким ученикам, где «уроки
        нескольким» читалось как групповое занятие. Самой пачки с 17.09.2026
        нет: она заводила урок без даты и ушла вместе с ним, а довод остался
        здесь и остался верным.
      */}
      <p className="muted">
        ⚠ У каждого ученика своя домашка: свои ответы, и правильные ответы вы
        показываете каждому отдельно. Ученики друг друга не видят.{" "}
        {props.tasks === null ? null : props.tasks.kind === "plan" ? (
          "Задания из заготовки при этом у всех одинаковые."
        ) : (
          /*
            ⚠️ «У каждого своё задание» без оговорки это неправда, и оговорка
            обязательна (раздел 0 фазы 2): у процентов различных задач всего 48,
            то есть на двенадцати учениках по пять заданий повтор ВОПРОСА между
            работами гарантирован арифметикой. Несписываемость держится не на
            числах, а на своих идентификаторах вариантов и своей перестановке у
            каждого.

            ⚠️ Слово «числа» отсюда снято английской волной: у заданий по
            английскому чисел нет вовсе, а обещание одно на обе генераторные
            ветки. Механизм при этом тот же, своё зерно на каждую работу.
          */
          <>
            Задания у каждого свои. На узких темах один и тот же вопрос может
            достаться двоим, но варианты ответа и их порядок у каждого свои.
          </>
        )}
      </p>
    </div>
  );
}
